import { collection, getDocs, doc, setDoc, query, getDocs as getDocsQuery, deleteDoc } from "firebase/firestore";
import { db, app, OperationType, handleFirestoreError } from "./firebase";
import { listRentlowCatalog } from "./data/list_rentlow";
import { defaultCmsPages } from "./data/defaultCms";
import { Booking, cleanFirestoreData } from "./types";

export async function restoreListRentlow() {
  try {
    console.log("Restoring official 'list rentlow' catalog sequence...");
    // 1. Fetch and delete all existing appliances to ensure a clean sequence slate
    const appliancesCol = collection(db, "appliances");
    const snapshot = await getDocs(appliancesCol);
    const deletePromises = snapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    // 2. Seed all official standard & upcoming appliances from the backup snapshot
    const uploadPromises = listRentlowCatalog.appliances.map(app => {
      return setDoc(doc(db, "appliances", app.id), {
        ...app,
        createdAt: new Date().toISOString()
      });
    });
    await Promise.all(uploadPromises);
    console.log("Successfully uploaded 'list rentlow' sequence.");
    return true;
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, "restore_list_rentlow");
    throw error;
  }
}

export async function ensureBookingsSeeded(): Promise<Booking[]> {
  try {
    const defaultBookings: Booking[] = [
      {
        id: "BOOKING-1785366076564",
        customerId: "cust_8602219782",
        customerName: "Anurag Agarwal",
        customerPhone: "8602219782",
        deliveryAddress: "Plot No 42, Near MP Nagar Zone-1",
        landmark: "Behind Dainik Bhaskar Office",
        pincode: "462011",
        serviceDate: "2026-08-02",
        city: "bhopal",
        totalRental: 2100,
        totalDeposit: 5500,
        status: "pending",
        adminMessage: "Your order is pending manual validation by the admin.",
        createdAt: "2026-08-02T10:30:00.000Z",
        items: [
          {
            applianceId: "wm-top-load-grey",
            name: "Top Load Fully Automatic Washing Machine (Grey)",
            quantity: 1,
            rentalPrice: 800,
            securityDeposit: 2000,
            imageUrl: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          },
          {
            applianceId: "fridge-single-190l",
            name: "Haier Sleek Silver Refrigerator (190L)",
            quantity: 1,
            rentalPrice: 700,
            securityDeposit: 2500,
            imageUrl: "https://images.unsplash.com/photo-1571175432267-efb0258593a6?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          },
          {
            applianceId: "purifier-kenstar-ro-uv",
            name: "Kenstar RO + UV Water Purifier",
            quantity: 1,
            rentalPrice: 600,
            securityDeposit: 1000,
            imageUrl: "https://images.unsplash.com/photo-1542013936693-884638332954?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      },
      {
        id: "BOOKING-1785200112233",
        customerId: "cust_9893012345",
        customerName: "Priya Sharma",
        customerPhone: "9893012345",
        deliveryAddress: "E-7/12, Arera Colony",
        landmark: "Near Hanuman Temple",
        pincode: "462016",
        serviceDate: "2026-07-28",
        city: "bhopal",
        totalRental: 1200,
        totalDeposit: 3000,
        status: "confirmed",
        adminMessage: "Order verified and scheduled for delivery.",
        createdAt: "2026-07-28T14:20:00.000Z",
        items: [
          {
            applianceId: "ac-1.5t-split-inverter",
            name: "Voltas 1.5 Ton 3 Star Inverter Split AC",
            quantity: 1,
            rentalPrice: 1200,
            securityDeposit: 3000,
            imageUrl: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      },
      {
        id: "BOOKING-1785100998877",
        customerId: "cust_9300691533",
        customerName: "Vikram Singh",
        customerPhone: "9300691533",
        deliveryAddress: "House 15, Englishpura",
        landmark: "Main Market",
        pincode: "466001",
        serviceDate: "2026-07-20",
        city: "sehore",
        totalRental: 950,
        totalDeposit: 2500,
        status: "completed",
        adminMessage: "Delivered and setup completed successfully.",
        createdAt: "2026-07-20T09:10:00.000Z",
        items: [
          {
            applianceId: "fridge-double-door-240l",
            name: "Whirlpool Double Door Refrigerator (240L)",
            quantity: 1,
            rentalPrice: 950,
            securityDeposit: 2500,
            imageUrl: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      },
      {
        id: "BOOKING-PENDING-9300691533",
        customerId: "cust_9300691533",
        customerName: "Test Customer (9300691533)",
        customerPhone: "9300691533",
        deliveryAddress: "Flat 204, Test Residency, MP Nagar",
        landmark: "Near Chetak Bridge",
        pincode: "462011",
        serviceDate: new Date().toISOString().split("T")[0],
        city: "bhopal",
        totalRental: 1400,
        totalDeposit: 3000,
        status: "pending",
        adminMessage: "Test order placed via 9300691533 pending approval.",
        createdAt: new Date().toISOString(),
        items: [
          {
            applianceId: "wm-top-load-grey",
            name: "Top Load Fully Automatic Washing Machine",
            quantity: 1,
            rentalPrice: 1400,
            securityDeposit: 3000,
            imageUrl: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      },
      {
        id: "BOOKING-1785000000001",
        customerId: "cust_9876543210",
        customerName: "Testing",
        customerPhone: "9876543210",
        deliveryAddress: "123 Test Street",
        landmark: "Near Test Tower",
        pincode: "462001",
        serviceDate: "2026-07-31",
        city: "bhopal",
        totalRental: 1400,
        totalDeposit: 3000,
        status: "confirmed",
        adminMessage: "Test order verified.",
        createdAt: "2026-07-31T11:00:00.000Z",
        items: [
          {
            applianceId: "wm-top-load-grey",
            name: "Top Load Fully Automatic Washing Machine",
            quantity: 1,
            rentalPrice: 1400,
            securityDeposit: 3000,
            imageUrl: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      },
      {
        id: "BOOKING-1785000000002",
        customerId: "cust_9300691533",
        customerName: "Rentlow Admin",
        customerPhone: "9300691533",
        deliveryAddress: "Store Warehouse, MP Nagar",
        landmark: "Zone-1",
        pincode: "462011",
        serviceDate: "2026-07-24",
        city: "bhopal",
        totalRental: 600,
        totalDeposit: 1500,
        status: "confirmed",
        adminMessage: "Admin demo booking.",
        createdAt: "2026-07-24T16:00:00.000Z",
        items: [
          {
            applianceId: "purifier-kenstar-ro-uv",
            name: "Kenstar RO Water Purifier",
            quantity: 1,
            rentalPrice: 600,
            securityDeposit: 1500,
            imageUrl: "https://images.unsplash.com/photo-1542013936693-884638332954?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      },
      {
        id: "BOOKING-1785000000003",
        customerId: "cust_9300691533",
        customerName: "Rentlow Admin",
        customerPhone: "9300691533",
        deliveryAddress: "Store Warehouse, MP Nagar",
        landmark: "Zone-1",
        pincode: "462011",
        serviceDate: "2026-07-24",
        city: "bhopal",
        totalRental: 5800,
        totalDeposit: 9500,
        status: "confirmed",
        adminMessage: "Admin bulk booking.",
        createdAt: "2026-07-24T12:00:00.000Z",
        items: [
          {
            applianceId: "ac-1.5t-split-inverter",
            name: "Voltas 1.5 Ton Split AC (x2)",
            quantity: 2,
            rentalPrice: 2400,
            securityDeposit: 6000,
            imageUrl: "https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          },
          {
            applianceId: "fridge-double-door-240l",
            name: "Double Door Refrigerator",
            quantity: 2,
            rentalPrice: 3400,
            securityDeposit: 3500,
            imageUrl: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      },
      {
        id: "BOOKING-1785000000004",
        customerId: "cust_9300691533",
        customerName: "Customer (9300691533)",
        customerPhone: "9300691533",
        deliveryAddress: "Main Market, Sehore",
        landmark: "Near Bus Stand",
        pincode: "466001",
        serviceDate: "2026-07-01",
        city: "sehore",
        totalRental: 600,
        totalDeposit: 1000,
        status: "confirmed",
        adminMessage: "Order confirmed.",
        createdAt: "2026-07-01T15:00:00.000Z",
        items: [
          {
            applianceId: "purifier-kenstar-ro-uv",
            name: "Kenstar RO Water Purifier",
            quantity: 1,
            rentalPrice: 600,
            securityDeposit: 1000,
            imageUrl: "https://images.unsplash.com/photo-1542013936693-884638332954?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      },
      {
        id: "BOOKING-1785000000005",
        customerId: "cust_9300691533",
        customerName: "Rentlow Admin",
        customerPhone: "9300691533",
        deliveryAddress: "Arera Colony",
        landmark: "E-7 Sector",
        pincode: "462016",
        serviceDate: "2026-07-01",
        city: "bhopal",
        totalRental: 2000,
        totalDeposit: 3500,
        status: "confirmed",
        adminMessage: "Package order verified.",
        createdAt: "2026-07-01T10:00:00.000Z",
        items: [
          {
            applianceId: "wm-top-load-grey",
            name: "Top Load Washing Machine",
            quantity: 1,
            rentalPrice: 800,
            securityDeposit: 2000,
            imageUrl: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          },
          {
            applianceId: "fridge-single-190l",
            name: "Haier Single Door Refrigerator",
            quantity: 1,
            rentalPrice: 1200,
            securityDeposit: 1500,
            imageUrl: "https://images.unsplash.com/photo-1571175432267-efb0258593a6?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      },
      {
        id: "BOOKING-1785000000006",
        customerId: "cust_9876543210",
        customerName: "Aashu",
        customerPhone: "9876543210",
        deliveryAddress: "Kolar Road",
        landmark: "Near Fine Avenue",
        pincode: "462042",
        serviceDate: "2026-06-30",
        city: "bhopal",
        totalRental: 800,
        totalDeposit: 2000,
        status: "completed",
        adminMessage: "Order completed.",
        createdAt: "2026-06-30T18:00:00.000Z",
        items: [
          {
            applianceId: "wm-top-load-grey",
            name: "Top Load Washing Machine",
            quantity: 1,
            rentalPrice: 800,
            securityDeposit: 2000,
            imageUrl: "https://images.unsplash.com/photo-1626806787461-102c1bfaaea1?auto=format&fit=crop&w=600&q=80",
            rentType: "month",
            duration: 1
          }
        ]
      }
    ];

    // Filter out any bookings deleted by the user or admin
    const deletedIdsStr = typeof localStorage !== "undefined" ? localStorage.getItem("rentlow_deleted_booking_ids") : null;
    let deletedIds: string[] = [];
    if (deletedIdsStr) {
      try { deletedIds = JSON.parse(deletedIdsStr); } catch (e) {}
    }

    const activeDefaultBookings = defaultBookings.filter(b => !deletedIds.includes(b.id));

    // Also sync to local storage cache so offline / initial state has these bookings immediately
    try {
      const existingLocalStr = localStorage.getItem("rentlow_local_bookings");
      let localList: any[] = [];
      if (existingLocalStr) {
        try { localList = JSON.parse(existingLocalStr); } catch (e) {}
      }
      
      let localChanged = false;
      activeDefaultBookings.forEach(defB => {
        const idx = localList.findIndex(b => b && b.id === defB.id);
        if (idx === -1) {
          localList.push(defB);
          localChanged = true;
        }
      });

      // Filter localList to ensure deleted IDs are purged
      if (deletedIds.length > 0) {
        const origLen = localList.length;
        localList = localList.filter(b => b && !deletedIds.includes(b.id));
        if (localList.length !== origLen) localChanged = true;
      }

      if (localChanged) {
        localStorage.setItem("rentlow_local_bookings", JSON.stringify(localList));
      }
    } catch (e) {
      console.warn("Could not sync local bookings cache:", e);
    }

    for (const booking of activeDefaultBookings) {
      const ref = doc(db, "bookings", booking.id);
      await setDoc(ref, cleanFirestoreData(booking), { merge: true });
    }
    console.log("Successfully ensured customer bookings data in Firestore.");
    return activeDefaultBookings;
  } catch (err) {
    console.warn("Could not ensure customer bookings in Firestore:", err);
    return [];
  }
}

export async function ensureAppliancesSeeded() {
  const activeProjectId = app?.options?.projectId;
  if (activeProjectId === "rentlow-3551b") {
    return;
  }
  try {
    const snap = await getDocs(collection(db, "appliances"));
    if (snap.empty) {
      console.log("Appliances collection is empty. Auto-seeding default catalog...");
      const promises = listRentlowCatalog.appliances.map(appItem => 
        setDoc(doc(db, "appliances", appItem.id), appItem)
      );
      await Promise.all(promises);
      console.log("Successfully auto-seeded appliances inventory into Firestore.");
    }
  } catch (err) {
    console.error("Failed to ensure appliances seeded:", err);
  }
}

export async function seedDatabaseIfEmpty(force = false) {
  // Double-layer safety constraint: NEVER auto-seed or modify the production database
  const activeProjectId = app?.options?.projectId;
  if (activeProjectId === "rentlow-3551b") {
    console.warn("Production database detected ('rentlow-3551b'). Aborting automatic seeding operation to safeguard production catalogs.");
    return;
  }

  if (!force) {
    let categoriesSnapshot;
    let appliancesSnapshot;
    try {
      const categoriesCol = collection(db, "categories");
      categoriesSnapshot = await getDocs(categoriesCol);
      const appliancesCol = collection(db, "appliances");
      appliancesSnapshot = await getDocs(appliancesCol);
    } catch (error) {
      handleFirestoreError(error, OperationType.GET, "categories/appliances");
      return;
    }
    
    if (!categoriesSnapshot.empty && !appliancesSnapshot.empty) {
      console.log("Database already seeded with categories and appliances.");
      return;
    }
  }

  try {
    console.log("Seeding database with default Rentlow content...");

    // 1. Seed Cities
    const defaultCities = [
      { id: "bhopal", name: "Bhopal" },
      { id: "sehore", name: "Sehore" }
    ];

    // 2. Seed Categories
    const defaultCategories = [
      { id: "fridge", name: "Fridge", slug: "fridge", iconName: "Refrigerator" },
      { id: "washing-machine", name: "Washing Machine", slug: "washing-machine", iconName: "WashingMachine" },
      { id: "cooler", name: "Cooler", slug: "cooler", iconName: "Wind" },
      { id: "microwave", name: "Microwave", slug: "microwave", iconName: "Microwave" },
      { id: "water-purifier", name: "Water Purifier", slug: "water-purifier", iconName: "Droplet" },
      { id: "led-tv", name: "Smart LED TV", slug: "led-tv", iconName: "Tv" },
      { id: "furniture", name: "Furniture", slug: "furniture", iconName: "Sofa" },
      { id: "induction", name: "Induction", slug: "induction", iconName: "Zap" },
      { id: "ac", name: "AC", slug: "ac", iconName: "Snowflake" },
      { id: "room-heater", name: "Room Heater", slug: "room-heater", iconName: "Flame" },
      { id: "vacuum-cleaner", name: "Vacuum Cleaner", slug: "vacuum-cleaner", iconName: "Sparkles" }
    ];

    // 3. Seed Appliances (Exactly matching the list rentlow catalog)
    const defaultAppliances = listRentlowCatalog.appliances;

    // 4. Seed CMS Pages
    const defaultCms = defaultCmsPages;

    const promises: Promise<void>[] = [];

    defaultCities.forEach(city => {
      promises.push(setDoc(doc(db, "cities", city.id), city));
    });

    defaultCategories.forEach(cat => {
      promises.push(setDoc(doc(db, "categories", cat.id), cat));
    });

    defaultAppliances.forEach(app => {
      promises.push(setDoc(doc(db, "appliances", app.id), app));
    });

    defaultCms.forEach(cms => {
      promises.push(setDoc(doc(db, "cms", cms.id), cms));
    });

    // Write all general items in parallel (allowed while global settings don't exist yet)
    await Promise.all(promises);

    // 5. Seed Settings - Write this strictly last to finalize seeding and lock non-admin write permissions
    const defaultSettings = {
      instagramUrl: "https://instagram.com/rentlow.co.in",
      facebookUrl: "https://facebook.com/rentlow.co.in",
      twitterUrl: "https://twitter.com/rentlow_india",
      supportPhone: "+919300691533",
      whatsappPhone: "918770781490",
      seoTitle: "Rentlow - Quality Home Appliances on Rent in Bhopal & Sehore",
      seoDescription: "Rent fridges, washing machines, coolers, smart TVs, and water purifiers in Bhopal and Sehore at incredibly low prices with zero upfront payment and doorstep delivery.",
      adminSecretCode: "RENTLOW777", // Secret code to register as admin easily
      carouselIntervalMs: 5000,
      serviceTaxPercent: 0
    };

    await setDoc(doc(db, "settings", "global"), defaultSettings);
    console.log("Database successfully seeded.");
  } catch (error) {
    handleFirestoreError(error, OperationType.WRITE, "seed_individual");
  }
}
